import { z } from 'zod';
import { ianaTimezoneSchema } from '../timezone';

// ─── care appointment vocabularies ──────────────────────────────────────────
//
// Governed enums for `care_appointments`. The `as const` array drives BOTH the
// `text` + CHECK constraint rendered in the DB schema and any wire schema
// (ADR-056 Rule 8); before the legacy import needed a fourth value the
// vocabulary lived inline in the CHECK, which is exactly the drift this file
// exists to prevent. The row shape is NOT mirrored here — readers consume
// `typeof careAppointments.$inferSelect` (ADR-066 Rules 1 and 3).

/**
 * Which surface wrote the booking. This is the STORED vocabulary the column's
 * CHECK renders; the member-facing wire enum in `api/care-scheduling.ts` is a
 * deliberate subset, because an imported row never reaches a client read. `member-sheet`: the member booked it in the
 * app. `agent-tool`: a voice or chat agent booked it on the member's behalf.
 * `staff-atlas`: a care person booked it from the member page. `legacy-import`:
 * copied from backend2's `user_telemedicine_appointments` by the member
 * migration, with `source_ref` carrying the 1.0 row id so a re-run is
 * idempotent.
 *
 * A `legacy-import` row carries the Galapagos specialist its 1.0 practitioner
 * resolved to, like every other source. The one exception is a HISTORY-ONLY
 * row: a past 1.0 booking whose practitioner resolves to nobody (usually
 * someone who has left) is imported with no specialist, never as `booked` and
 * never attested — `care_appointments_specialist_required_check` holds every
 * other row to a specialist. Readers treat imported rows as the real
 * appointments they are — only the Healthie write-back excludes them — and
 * name a history-only row through `formerPractitionerName`.
 */
export const CARE_APPOINTMENT_SOURCE = [
  'member-sheet',
  'agent-tool',
  'staff-atlas',
  'legacy-import',
  'healthie-calendar',
] as const;
export const careAppointmentStoredSourceSchema = z.enum(CARE_APPOINTMENT_SOURCE);
export type CareAppointmentStoredSource = z.infer<typeof careAppointmentStoredSourceSchema>;

/** The import source value, named once so no mapper or reader spells it. */
export const CARE_APPOINTMENT_LEGACY_SOURCE = 'legacy-import' satisfies CareAppointmentStoredSource;

/**
 * The 1.0 practitioner role an imported appointment carries in `display_role`.
 * The raw backend2 enum, not display copy: `care_specialists.display_role` is
 * free text a human wrote, and turning these into member-facing words is a
 * localization change (ADR-017) the visibility follow-up owns.
 */
export const CARE_APPOINTMENT_LEGACY_ROLE = ['physician', 'quitCoach', 'careCoordinator'] as const;
export const careAppointmentLegacyRoleSchema = z.enum(CARE_APPOINTMENT_LEGACY_ROLE);
export type CareAppointmentLegacyRole = z.infer<typeof careAppointmentLegacyRoleSchema>;

/**
 * Provenance for an imported booking, held in the `source_metadata` jsonb.
 *
 * `recording` is a COORDINATE, never content: the migration references the 1.0
 * call recording where it already lives and copies nothing, so the shape is a
 * bucket plus the key prefix under which that appointment's objects sit.
 *
 * `noShow` is preserved because Galapagos's status vocabulary has no no-show
 * value — the row maps to `completed` and the fact survives here.
 *
 * `bookingSource`, `externalMeetingId` and `legacyStatus` are the raw 1.0
 * `source`, `externalMeetingId` and `status` columns verbatim, whatever the
 * mapper derived from them: `healthie_appointment_id` is only set when the
 * source was Healthie and the id has a Healthie shape, and `status` folds five
 * 1.0 values into three, so the raw values are the only way back.
 *
 * `practitionerUsername` (the 1.0 staff sign-in email, trimmed and
 * lower-cased) and `practitionerHealthieProviderId` are the stable keys a
 * former counsellor is grouped by when no Galapagos specialist is theirs;
 * `practitioner` is free display text and, for some 1.0 staff, a UUID.
 * Optional because rows imported before they were carried lack them.
 *
 * Not `.strict()`: this is stored provenance read back by a tolerant parse, so
 * a key a later import adds must not make an older row unreadable (ADR-135
 * Rule 1). The mapper is the only writer and always supplies every key.
 */
export const careAppointmentSourceMetadataSchema = z.object({
  legacy: z.object({ table: z.string().min(1), id: z.number().int() }),
  practitioner: z.string().nullable(),
  practitionerUsername: z.string().nullable().optional(),
  practitionerHealthieProviderId: z.string().nullable().optional(),
  reason: z.string().nullable(),
  bookingSource: z.string().nullable(),
  externalMeetingId: z.string().nullable(),
  legacyStatus: z.string().nullable(),
  // Validated rather than carried as free text: a stored zone is a persisted
  // timezone value like any other, and an abbreviation or offset copied from
  // 1.0 would be exactly the shape the shared schema exists to keep out of
  // the database (ADR-095 Rule 1).
  timezone: ianaTimezoneSchema.nullable(),
  noShow: z.boolean(),
  /** Raw 1.0 `initialCallType` when the seam carried it (`video`, `audio`, `phone`). */
  initialCallType: z.string().nullable().optional(),
  recording: z.object({ bucket: z.string().min(1), keyPrefix: z.string().min(1) }).optional(),
});
export type CareAppointmentSourceMetadata = z.infer<typeof careAppointmentSourceMetadataSchema>;

/**
 * How the member and care team meet for a booked appointment. Stored on
 * `care_appointments.modality` with a CHECK derived from this array (ADR-056).
 * Legacy-import rows may remain null; every other source defaults to `video`.
 */
export const CARE_APPOINTMENT_MODALITY = ['video', 'phone', 'in_person'] as const;
export const careAppointmentModalitySchema = z.enum(CARE_APPOINTMENT_MODALITY);
export type CareAppointmentModality = z.infer<typeof careAppointmentModalitySchema>;

export const HEALTHIE_INBOUND_PENDING_LIFECYCLE = [
  'care.appointment.booked',
  'care.appointment.cancelled',
  'care.appointment.rescheduled',
] as const;
export const healthieInboundPendingLifecycleSchema = z.enum(HEALTHIE_INBOUND_PENDING_LIFECYCLE);

/** Stored on inbound Healthie-calendar rows only (not `.strict()` — tolerant read). */
export const careAppointmentHealthieCalendarMetadataSchema = z.object({
  healthie: z.object({
    updatedAt: z.string().min(1),
    /** Healthie `contact_type` label, stored verbatim on ingest; modality UX is out of scope here. */
    contactType: z.string().optional(),
    /** ISO instant when ingest side effects (lifecycle, assignment, reminders) last completed. */
    sideEffectsAppliedAt: z.string().min(1).optional(),
    /** Set when lifecycle publish fails so a retry can emit the same operation. */
    pendingLifecycle: healthieInboundPendingLifecycleSchema.optional(),
  }),
  recurring: z
    .object({
      position: z.number().int().positive().optional(),
      interval: z.string().optional(),
    })
    .optional(),
});
export type CareAppointmentHealthieCalendarMetadata = z.infer<
  typeof careAppointmentHealthieCalendarMetadataSchema
>;

export type CareAppointmentStoredSourceMetadata =
  | CareAppointmentSourceMetadata
  | CareAppointmentHealthieCalendarMetadata;

/**
 * Whether an appointment names a specialist. Only a history-only imported row
 * (see `CARE_APPOINTMENT_SOURCE`) has none; a path that needs the specialist —
 * a live booking, an attestation, a mirror — narrows through this rather than
 * asserting the column non-null.
 */
export function hasSpecialist<T extends { specialistId: string | null }>(
  row: T,
): row is T & { specialistId: string } {
  return row.specialistId !== null;
}

/** Table name prefix for legacy-import rows copied from backend2 appointments. */
export const CARE_APPOINTMENT_LEGACY_SOURCE_REF_TABLE = 'user_telemedicine_appointments';

/**
 * Parse the 1.0 appointment id from `care_appointments.source_ref` when it matches
 * `user_telemedicine_appointments:{id}`; otherwise null.
 */
export function legacyAppointmentIdFromSourceRef(
  sourceRef: string | null | undefined,
): number | null {
  if (sourceRef == null || sourceRef === '') return null;
  const prefix = `${CARE_APPOINTMENT_LEGACY_SOURCE_REF_TABLE}:`;
  if (!sourceRef.startsWith(prefix)) return null;
  const idPart = sourceRef.slice(prefix.length);
  if (!/^[1-9]\d*$/.test(idPart)) return null;
  return Number.parseInt(idPart, 10);
}
